import { isAxiosError } from 'axios'

const DEFAULT_MESSAGE = 'Failed to update expense'

/**
 * The error body returned by pay-endpoint for ApplicationErrors.
 * `info` is the `clientInfo` the backend attached to the error — for
 * inspect/commit validation failures it carries a per-field `fields` map
 * with `reason` strings meant to be displayed to the user.
 */
type TransactionEditErrorBody = {
  message?: string
  info?: {
    fields?: Record<string, { reason?: string }>
  }
}

/**
 * Extracts the backend's per-field error reasons (`clientInfo.fields`) from a
 * transaction edit failure, keyed by transaction field name. Returns null when
 * the error carries no usable field map. Each value is the reason to display
 * under the matching form field.
 *
 * @example
 * ```ts
 * const fields = getTransactionEditErrorFields(error) // { paymentMethod: '...' }
 * for (const [field, reason] of Object.entries(fields)) {
 *   form.setError(formFieldFor(field), { message: reason })
 * }
 * ```
 */
export function getTransactionEditErrorFields(error: unknown): Record<string, string> | null {
  if (!isAxiosError<TransactionEditErrorBody>(error)) {
    return null
  }

  const fields = error.response?.data?.info?.fields
  if (!fields || typeof fields !== 'object') {
    return null
  }

  const result: Record<string, string> = {}
  for (const [field, info] of Object.entries(fields)) {
    const reason = info?.reason?.trim()
    if (reason) {
      result[field] = reason
    }
  }

  return Object.keys(result).length > 0 ? result : null
}

/**
 * Maps a transaction edit (inspect/commit) failure to a user-facing message.
 *
 * The backend rejects invalid commits with an ApplicationError whose
 * `clientInfo` is serialized into the HTTP error body under `info.fields`
 * (e.g. `{ paymentMethod: { reason: '...' } }`). When present, those reasons
 * are joined verbatim; otherwise the server message or a generic fallback is
 * used. Prefer {@link getTransactionEditErrorFields} when the error should be
 * displayed under individual form fields.
 */
export function getTransactionEditErrorMessage(error: unknown): string {
  if (!isAxiosError<TransactionEditErrorBody>(error)) {
    return DEFAULT_MESSAGE
  }

  const fieldErrors = getTransactionEditErrorFields(error)
  if (fieldErrors) {
    return Object.values(fieldErrors).join(' ')
  }

  const message = error.response?.data?.message?.trim()
  if (message && message.length > 0 && message.length < 240) {
    return message
  }

  return DEFAULT_MESSAGE
}

/**
 * True when inspect rejected the request because the expense cannot be edited
 * (400/403), as opposed to a transient failure that should be retried.
 */
export function isTransactionEditInspectBlockedError(error: unknown): boolean {
  if (!isAxiosError<TransactionEditErrorBody>(error)) {
    return false
  }

  return error.response?.status === 400 || error.response?.status === 403
}

/**
 * Extract the error message from the inspect endpoint when it throws a 400 or 403 error.
 * The backend returns a message field in the body of the response, display that when available.
 * Returns null when the error is not an axios error, is neither a 400 nor a 403, or carries no
 * usable message.
 *
 * @example
 * ```ts
 * const reason = getTransactionEditInspectBlockedMessage(error)
 * if (reason) {
 *   // show a banner: "This expense can't be edited on a completed charter"
 * }
 * ```
 */
export function getTransactionEditInspectBlockedMessage(error: unknown): string | null {
  if (!isTransactionEditInspectBlockedError(error) || !isAxiosError<TransactionEditErrorBody>(error)) {
    return null
  }

  const message = error.response?.data?.message?.trim()
  return message && message.length > 0 ? message : null
}
